import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { ReactComponent as SendIcon } from './icons/send_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as MicIcon } from './icons/mic_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as MicOffIcon } from './icons/mic_off_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import { createBrandTheme } from './theme';
import { useBrand, resolveMediaUrl } from './BrandContext';
import CircularProgress from '@mui/material/CircularProgress';
import rehypeRaw from 'rehype-raw';
import { ReactComponent as HeadsetMicIcon } from './icons/headset_mic_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as HelpOutlineIcon } from './icons/info_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ThreeDots } from 'react-loader-spinner';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { ReactComponent as VoiceOffIcon } from './icons/voice_over_off_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as VoiceOnIcon } from './icons/record_voice_over_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import ProductMediaSidebar from './ProductMediaSidebar';
import AppointmentForm from './AppointmentForm';
import PhoneIcon from '@mui/icons-material/Phone';
import { mergeProductWithCatalog, productHasMedia } from './lib/resolveProductMedia';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const AVATAR_SIZE = 36;

function BotAvatar({ src, name, size = AVATAR_SIZE }) {
  const initial = String(name || 'A').trim().charAt(0).toUpperCase() || 'A';
  const frameSx = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    flex: '0 0 auto',
    alignSelf: 'flex-start',
    aspectRatio: '1 / 1',
    borderRadius: '50%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    mr: 1.25,
    mt: 0.25,
  };

  if (src) {
    return (
      <Box sx={frameSx}>
        <Box
          component="img"
          src={src}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...frameSx,
        bgcolor: 'primary.main',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.round(size * 0.42),
        lineHeight: 1,
        letterSpacing: 0,
        userSelect: 'none',
      }}
    >
      <Box component="span" sx={{ lineHeight: 1, display: 'block' }}>
        {initial}
      </Box>
    </Box>
  );
}

function ChatMessage({ message }) {
  return (
    <Typography
      component="div"
      sx={{
        fontSize: '0.9rem',
        lineHeight: 1.5,
        color: 'inherit',
        '& p': { margin: 0 },
        '& p + p': { mt: 0.75 },
      }}
    >
      <ReactMarkdown rehypePlugins={[rehypeRaw]}>{message}</ReactMarkdown>
    </Typography>
  );
}

const ChatBot = () => {
  const { config } = useBrand();
  const theme = createBrandTheme(config);
  const accentColor = config?.secondaryColor || theme.palette.secondary.main;
  const logoUrl = resolveMediaUrl(config?.logoUrl);
  const avatarUrl = resolveMediaUrl(config?.avatarUrl);
  const introVideoUrl = resolveMediaUrl(config?.introVideoUrl);
  const welcomeText = config?.welcomeMessage || 'Ciao! Come posso aiutarti?';

  const [messages, setMessages] = useState(() => {
  const savedMessages = sessionStorage.getItem('chatMessages');
  return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [isTtsActive, setIsTtsActive] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(null); 

  const [productData, setProductData] = useState([]);
  const [isProductDataLoaded, setIsProductDataLoaded] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isVoiceAssistantActive, setIsVoiceAssistantActive] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const recentLinks = useRef([]);
  const endOfMessagesRef = useRef(null);
  const videoRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const isVoiceAssistantActiveRef = useRef(isVoiceAssistantActive);
  const isProcessingResponseRef = useRef(false);
  const audioRef = useRef(null);
  const helpOverlayRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [productMedia, setProductMedia] = useState(null);
  const toggleSidebar = () => {
  setIsSidebarOpen(!isSidebarOpen);
  };
  
  useEffect(() => {
  isVoiceAssistantActiveRef.current = isVoiceAssistantActive;
  }, [isVoiceAssistantActive]);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/public/products`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        setProductData(data);
        setIsProductDataLoaded(true);

        const initialMessage = {
          sender: 'bot',
          text: "Vuoi attivare l'assistenza avanzata?",
          options: [
            { text: 'Sì', action: 'activateAdvancedAssistance' },
            { text: 'No', action: 'startNormalConversation' },
          ],
        };
        setMessages([initialMessage]);
      } catch (error) {
        console.error('Errore nel caricamento dei dati dei prodotti:', error);
        setMessages([
          {
            sender: 'bot',
            text: "C'è stato un problema nel caricamento dei dati dei prodotti. Riprova più tardi.",
          },
        ]);
      }
    };

    initializeChat();
  }, []);

  useEffect(() => {
    setWelcomeMessage(welcomeText);
  }, [welcomeText]);
  
  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const handleLinkClick = (event) => {
      if (event.target.tagName === 'A') {
        event.preventDefault();
        const url = event.target.href;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };
  
    document.addEventListener('click', handleLinkClick);
  
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  useEffect(() => {
    if (!isTtsActive && audioRef.current) {
      // Se la TTS è stata spenta e c'è un audio in riproduzione, fermalo
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // opzione per riportarlo all'inizio
      audioRef.current = null;
    }
  }, [isTtsActive]);  

  const handleHelpClick = () => {
    setShowHelp((prevShowHelp) => !prevShowHelp);
  };  

  const HelpOverlay = ({ onClose }) => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(8px)',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3,
        boxSizing: 'border-box',
        overflow: 'auto',
      }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
        Guida all'uso
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 320, mb: 2 }}>
        Benvenuto nel chatbot. Ecco come usarlo:
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.5,
          fontSize: '0.9rem',
          lineHeight: 1.7,
          color: 'text.primary',
          maxWidth: 340,
        }}
      >
        <li>Puoi chiedere informazioni sui nostri prodotti e servizi.</li>
        <li>Clicca sull'icona del <b>Microfono</b> per attivare l'assistente vocale.</li>
        <li>Scrivi un messaggio nella barra in basso per fare domande.</li>
        <li>Clicca sull'icona <b>cuffie</b> per fissare un appuntamento o lasciare i recapiti.</li>
        {config?.centralinoEnabled && config?.centralinoPhone && (
          <li>
            Clicca sul <b>telefono</b> per chiamare il centralino {config.centralinoPhone}
            {config.centralinoHours ? ` (${config.centralinoHours})` : ''}.
          </li>
        )}
      </Box>
      <Button variant="contained" onClick={onClose} sx={{ mt: 3, borderRadius: 5, px: 3, textTransform: 'none' }}>
        Chiudi
      </Button>
    </Box>
  );
  
  const initializeRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Il tuo browser non supporta il riconoscimento vocale.');
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.lang = 'it-IT';
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = false;

    recognitionInstance.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      handleVoiceInput(transcript);
    };

    recognitionInstance.onerror = (event) => {
      console.error('Errore nel riconoscimento vocale:', event.error);
    };

    recognitionInstance.onend = () => {
      clearTimeout(silenceTimeoutRef.current);
      if (isVoiceAssistantActiveRef.current && !isProcessingResponseRef.current) {
        try {
          recognitionInstance.start();
        } catch (error) {
          console.error('Errore nel riavvio del riconoscimento vocale:', error);
        }
      }
    };

    recognitionRef.current = recognitionInstance;
  };

  const startVoiceRecognition = () => {
    if (!recognitionRef.current) {
      initializeRecognition();
    }

    setIsVoiceAssistantActive(true);
    isVoiceAssistantActiveRef.current = true;

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Errore nell'avvio del riconoscimento vocale:", error);
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsVoiceAssistantActive(false);
    isVoiceAssistantActiveRef.current = false;
  };

  const toggleVoiceRecognition = () => {
    if (isVoiceAssistantActive) {
      stopVoiceRecognition();
    } else {
      // Sto per attivare il microfono, spengo la TTS
      if (isTtsActive) setIsTtsActive(false);
      startVoiceRecognition();
    }
  };
  
  const handleVoiceInput = async (transcript) => {
    let correctedTranscript = transcript;
    (config?.brandSttAliases || []).forEach((alias) => {
      if (!alias?.from || !alias?.to) return;
      const escaped = String(alias.from).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      correctedTranscript = correctedTranscript.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), alias.to);
    });
  
    const isOpenLinkCommand = openLinkByVoiceCommand(correctedTranscript);
  
    isProcessingResponseRef.current = true;
    recognitionRef.current.stop();
  
    if (!isOpenLinkCommand) {
      const userMessage = { text: correctedTranscript, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
  
      if (!showUserForm) {
        // Ottieni direttamente la risposta del bot
        const botResponse = await processUserMessage(correctedTranscript);
  
        // Spegni il microfono se attivo
        if (isVoiceAssistantActive) {
          stopVoiceRecognition();
        }
  
        // Attiva il TTS e leggila direttamente
        setIsTtsActive(true);
        playMessageAsAudio(botResponse);
      }
    }
  
    isProcessingResponseRef.current = false;
  };
         
  const handleUserInput = async () => {
    if (!userInput.trim()) return;
  
    // Salva il messaggio dell'utente prima di svuotare il campo
    const userMessage = { text: userInput, sender: 'user' };
  
    // Svuota il campo di input subito
    setUserInput('');
  
    // Aggiungi il messaggio dell'utente alla lista dei messaggi
    setMessages((prevMessages) => [...prevMessages, userMessage]);
  
    // Controlla se l'utente vuole fissare un appuntamento
    if (userInput.toLowerCase().includes('prendiamo un appuntamento')) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'Vuoi fissare un appuntamento?', options: [{ text: 'Conferma', action: 'confirmAppointment' }, { text: 'Annulla', action: 'cancelAppointment' }] },
      ]);
    } else {
      // Invoca la funzione per processare il messaggio
      await processUserMessage(userInput);
    }
  };
  
  const enrichProductLink = (responseText) => {
    let enrichedText = responseText;
    recentLinks.current = []; // Resetta la lista dei link
  
    // Regex per trovare i link in Markdown
    const linkRegex = /\[([^\]]+)\]\((http[^\)]+)\)/g;
  
    let matches = [];
    let match;
  
    // Trova tutti i link nella risposta
    while ((match = linkRegex.exec(responseText)) !== null) {
      matches.push({
        fullMatch: match[0],
        linkText: match[1],
        url: match[2],
      });
    }
  
    // Processa i link trovati
    matches.forEach((match, index) => {
      const linkNumber = index + 1;
      recentLinks.current.push(match.url); // Memorizza l'URL
  
      // Crea un link numerato
      const numberedLink = `[${match.linkText} [Link ${linkNumber}]](${match.url})`;
  
      // Sostituisci il link originale con quello numerato
      enrichedText = enrichedText.replace(match.fullMatch, numberedLink);
    });
  
    return enrichedText;
  };
                      
  const processUserMessage = async (messageText) => {
    setIsLoading(true);
    const aiResponse = await getChatGPTResponse(messageText);
    // Se la risposta è strutturata, popola recentLinks.current con i link dei prodotti
  if (aiResponse.type === "structured" && aiResponse.products && aiResponse.products.length > 0) {
    recentLinks.current = aiResponse.products
      .map((prod) => prod["link puro"] || prod.pageUrl || prod.urlPagina)
      .filter(Boolean);
  } else {
    recentLinks.current = []; // Altrimenti svuotala
  }
  
  let botMessage;
  if (aiResponse.type === "structured") {
    // Memorizza direttamente l'oggetto strutturato
    botMessage = { ...aiResponse, sender: 'bot' };
  } else {
    // Se plain, crea il messaggio con il testo semplice
    botMessage = { text: aiResponse.text, sender: 'bot' };
  }
    setMessages((prevMessages) => [...prevMessages, botMessage]);
    setIsLoading(false);
    return aiResponse;
  };
  
  const pronunciationMap = {
    'HyBiz': 'Hai-Biz',
    'XWEB': 'Ix-Web',
    'XCOMMERCE': 'Ix-Commerce',
    'XASSISTANT': 'Ix-Assistant',
    'Innovative Solution': 'Innovativ Soluscion',
    'AR': 'E-Ar',
    'VR': 'Vi-Ar',
    'EXR': 'I-Ex-Ar',
    'Solution': 'Soluscion'
};

const adjustPronunciation = (text) => {
    Object.keys(pronunciationMap).forEach((key) => {
        const regex = new RegExp(`\\b${key}\\b`, 'gi');
        text = text.replace(regex, pronunciationMap[key]);
    });
    return text;
};

const playMessageAsAudio = async (originalMessage) => {
  try {
    console.log('Testo originale per la TTS:', originalMessage);
    let ttsText = '';

    if (typeof originalMessage === 'string') {
      ttsText = originalMessage;
    } else if (typeof originalMessage === 'object' && originalMessage.text) {
      // Aggiunge il testo principale
      ttsText += originalMessage.text + ' ';
      
      // Aggiunge, per ciascun prodotto, nome, descrizione e caratteristiche tecniche
      if (originalMessage.products && originalMessage.products.length > 0) {
        originalMessage.products.forEach(product => {
          if (product.name) {
            ttsText += product.name + '. ';
          }
          if (product.description) {
            ttsText += product.description + '. ';
          }
          if (product["caratteristiche tecniche"]) {
            ttsText += product["caratteristiche tecniche"] + '. ';
          }
        });
      }
      
      // Aggiunge il prompt finale
      if (originalMessage.prompt) {
        ttsText += originalMessage.prompt;
      }
    } else {
      ttsText = '';
    }

    // Applica la correzione della pronuncia
    ttsText = adjustPronunciation(ttsText);
    console.log('Testo per la TTS dopo adjustPronunciation:', ttsText);

    // Pulisce il testo
    ttsText = ttsText
      .replace(/\*\*(.*?)\*\*/g, '$1') // Rimuove eventuali marcatori di grassetto
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1') // Rimuove il markdown dei link
      .replace(/\[Link\s+\d+\]/g, '') // Rimuove il testo dei link numerati
      .replace(/https?:\/\/\S+/g, '') // Rimuove URL rimanenti
      .replace(/\*/g, '')
      .replace(/[()]/g, '')
      .trim()
      .replace(/\s+/g, ' ');

    console.log('Testo dopo la pulizia:', ttsText);

    // Richiesta al backend per la sintesi vocale
    const response = await fetch(`${BACKEND_URL}/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: ttsText })
    });
  
    if (!response.ok) {
      throw new Error('Errore durante la sintesi vocale');
    }
  
    const data = await response.json();
    const audioBlob = base64ToBlob(data.audioContent, 'audio/mp3');
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
  
    audio.onended = () => {
      setIsTtsActive(false);
      // Riattiva il microfono solo se l'utente non l'ha disattivato
      if (isVoiceAssistantActiveRef.current) {
        startVoiceRecognition();
      }
    };
  
    audio.play();
  
  } catch (error) {
    console.error('Errore TTS:', error);
  }
};

  // Utility per convertire base64 in Blob
  function base64ToBlob(base64, type = 'application/octet-stream') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  }  
        
  // Funzione per aprire un link specifico in base al comando vocale
  const openLinkByVoiceCommand = (transcript) => {
    const match = transcript.match(/apri link (?:numero )?(\d+)/i);
    if (match) {
      const linkNumber = parseInt(match[1], 10);
      if (linkNumber > 0 && linkNumber <= recentLinks.current.length) {
        const url = recentLinks.current[linkNumber - 1];
        window.open(url, '_blank', 'noopener,noreferrer');
        console.log(`Aperto link ${linkNumber}: ${url}`);
  
        // Disabilita il riconoscimento vocale quando si apre un link
        stopVoiceRecognition();
  
        return true; // Indica che è stato riconosciuto ed eseguito un comando per aprire un link
      } else {
        console.log(`Link numero ${linkNumber} non trovato.`);
        return false;
      }
    }
    return false; // Nessun comando per aprire un link riconosciuto
  };
                   
  const handleUserFormSubmit = async (formData) => {
    const userData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        contactReason: formData.contactReason
    };

    try {
      const response = await fetch(`${BACKEND_URL}/send-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData,
          appointmentDateTime: formData.appointmentDateTime,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: formData.appointmentDateTime
            ? 'Appuntamento confermato. Ti abbiamo inviato i dettagli.'
            : 'La tua richiesta è stata inviata con successo e verrai presto ricontattato!' },
        ]);
        setShowUserForm(false);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: data.error || 'Si è verificato un errore durante l\'invio del modulo, riprova più tardi.' },
        ]);
      }
    } catch (error) {
      console.error('Errore:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'C\'è stato un problema nella comunicazione con il server. Riprova più tardi.' },
      ]);
    }
  };
  
  const handleOptionSelected = (action) => {
    if (action === 'confirmAppointment') {
      setShowUserForm(true);  // Mostra il form per l'appuntamento
    } else if (action === 'cancelAppointment') {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'Appuntamento annullato, continuiamo la chat.' }
      ]);
    } else if (action === 'activateAdvancedAssistance') {
      setMessages((prevMessages) => prevMessages.slice(0, -1));
      if (introVideoUrl) {
        playIntroVideo();
      } else {
        startVoiceRecognition();
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: welcomeMessage || welcomeText },
        ]);
      }
    } else if (action === 'startNormalConversation') {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: welcomeMessage || welcomeText }
      ]);
    }    
  };
  
  const playIntroVideo = () => {
    const videoMessage = {
      sender: 'bot',
      type: 'video',
      content: introVideoUrl,
    };
    setMessages((prevMessages) => [...prevMessages, videoMessage]);
  };

  const getChatGPTResponse = async (message) => {
    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, userId: 'user123' }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log("Risposta da OpenAI (dal backend):", data.reply);
  
      // Proviamo a fare il parsing della risposta come JSON
      try {
        const parsed = JSON.parse(data.reply);
        // Se parsed ha i campi attesi, aggiungiamo un campo "type"
        if (parsed && typeof parsed === 'object' && parsed.text) {
          parsed.type = "structured";
          if (Array.isArray(parsed.products)) {
            parsed.products = parsed.products.map((prod) => mergeProductWithCatalog(prod, productData));
          }
          return parsed;
        }
      } catch (e) {
        // Se il parsing fallisce, significa che il JSON è malformato o incompleto.
        console.error("Errore nel parsing del JSON dalla risposta dell'AI:", e);
        // Restituiamo un messaggio di errore specifico invece del JSON rotto.
        return { type: "plain", text: "Mi dispiace, ho avuto un problema nel formattare la risposta. Potresti provare a riformulare la tua domanda in modo più specifico?" };
      }
  
      // Fallback se il JSON è valido ma non ha la struttura attesa
      return { type: "plain", text: data.reply };
    } catch (error) {
      console.error('Error in getChatGPTResponse:', error);
      return { type: "plain", text: "Mi dispiace, c'è stato un errore nella comunicazione con il server." };
    }
  };
  
 // Funzione helper per escapare i caratteri speciali in una stringa
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const handleProductRecognition = useCallback((message) => {
  const text = message.text;
  let lastAcceptedEnd = 0;
  let segments = [];
  let matches = [];

  // Ordina i prodotti per lunghezza decrescente (i più lunghi prima)
  const sortedProductData = [...productData].sort(
    (a, b) => b.nomeProdotto.trim().length - a.nomeProdotto.trim().length
  );

  console.log("Processing message text:", text);

  // Per ogni prodotto, crea una regexp che:
  // • Inizi con un confine di parola (\b)
  // • Usi il nome escapato
  // • Richieda (lookahead) che dopo il nome ci sia uno spazio o la fine della stringa
  // I flag: g (global), i (case-insensitive), u (Unicode)
  sortedProductData.forEach((product) => {
    const productName = product.nomeProdotto.trim();
    const regex = new RegExp('\\b' + escapeRegExp(productName) + '(?=\\s|$)', 'giu');
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: regex.lastIndex,
        product,
        matchText: match[0]
      });
      console.log(`Found match for product "${productName}": "${match[0]}" at index ${match.index}`);
    }
  });

  // Ordina i match per indice di inizio
  matches.sort((a, b) => a.start - b.start);

  // Per evitare duplicazioni, teniamo traccia dei prodotti già matchati
  const matchedProducts = new Set();

  // Itera sui match per costruire i segmenti
  matches.forEach((m) => {
    // Se il match inizia dopo la fine dell'ultimo segmento accettato, aggiungi il testo intermedio
    if (m.start > lastAcceptedEnd) {
      const textSegment = text.slice(lastAcceptedEnd, m.start);
      segments.push({ type: 'text', text: textSegment });
      console.log("Created text segment:", textSegment);
    }
    // Se questo prodotto non è già stato matchato, aggiungilo come segmento
    const productKey = m.product.nomeProdotto.trim().toLowerCase();
    if (!matchedProducts.has(productKey)) {
      segments.push({ type: 'product', text: m.matchText, product: m.product });
      matchedProducts.add(productKey);
      console.log("Created product segment:", m.matchText, "for product:", m.product.nomeProdotto);
      lastAcceptedEnd = m.end;
    } else {
      console.log("Skipping duplicate product:", m.product.nomeProdotto);
    }
  });

  // Se c'è ancora del testo dopo l'ultimo match, aggiungilo
  if (lastAcceptedEnd < text.length) {
    const textSegment = text.slice(lastAcceptedEnd);
    segments.push({ type: 'text', text: textSegment });
    console.log("Created final text segment:", textSegment);
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', text });
    console.log("No segments created, using full text");
  }

  console.log("Final segments:", segments);
  return segments;
}, [productData]);

  const handleViewMedia = (product) => {
    const resolved = mergeProductWithCatalog(product, productData);
    setProductMedia({
      url3Dviewer: resolved.url3Dviewer,
      urlImage: resolved.urlImage,
      urlVideo: resolved.urlVideo,
    });
    setIsSidebarOpen(true);
  };
  
  // Definizione della funzione helper per il rendering dei segmenti
  const renderStructuredMessage = (message) => {
    // Visualizza il testo principale
    const parts = [];
    parts.push(
      <Typography key="text" sx={{ whiteSpace: 'pre-line', fontSize: '0.9rem', lineHeight: 1.5, color: 'inherit' }}>
        {message.text}
      </Typography>
    );
    
    // Se ci sono prodotti, itera e visualizzali
    if (message.products && message.products.length > 0) {
      message.products.forEach((prod, index) => {
        const resolved = mergeProductWithCatalog(prod, productData);
        const pageUrl = resolved["link puro"];
        parts.push(
          <Box
            key={(resolved.name || prod.name || 'product') + index}
            sx={{
              mt: 1.25,
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: 'rgba(15, 23, 42, 0.035)',
              border: '1px solid rgba(15, 23, 42, 0.08)',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', mb: 0.4 }}>
              {resolved.name}
            </Typography>
            {resolved.description ? (
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.45 }}>
                {resolved.description}
              </Typography>
            ) : null}
            {resolved["caratteristiche tecniche"] ? (
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5 }}>
                {resolved["caratteristiche tecniche"]}
              </Typography>
            ) : null}
            {pageUrl ? (
              <Typography sx={{ fontSize: '0.82rem', mt: 0.75 }}>
                <a href={pageUrl} target="_blank" rel="noopener noreferrer">
                  Apri scheda prodotto
                </a>
              </Typography>
            ) : null}
            {productHasMedia(resolved) ? (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleViewMedia(resolved)}
                sx={{
                  mt: 1,
                  borderRadius: 5,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  px: 1.5,
                }}
              >
                Visualizza Media
              </Button>
            ) : null}
          </Box>
        );
      });
    }
    
    // Visualizza il prompt se presente
    if (message.prompt) {
      parts.push(
        <Typography key="prompt" sx={{ fontStyle: 'italic', mt: 1.25, fontSize: '0.85rem', color: 'text.secondary' }}>
          {message.prompt}
        </Typography>
      );
    }
    
    return parts;
  };
  
  const renderMessage = (message) => {
    return renderStructuredMessage(message);
  };

  const headerIconSx = (active) => ({
    color: active ? accentColor : 'text.secondary',
    width: 36,
    height: 36,
    borderRadius: '10px',
    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
  });

  return (
    <ThemeProvider theme={theme}>
      {!isProductDataLoaded ? (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            width: '420px',
            height: '100%',
            maxHeight: '100%',
            minHeight: '400px',
            overflow: 'visible',
            '@media (max-width: 600px)': {
              width: '100%',
              height: '100vh', // Altezza intera sui dispositivi mobili
              right: 0,
              bottom: 0,
            },
          }}
        >
          <ProductMediaSidebar
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            productMedia={productMedia}
            productData={productData}
          />
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '24px',
              zIndex: 1000,
              overflow: 'hidden',
              bgcolor: '#fff',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 18px 50px rgba(15, 23, 42, 0.16)',
              '@media (max-width: 600px)': {
                borderRadius: 0,
                border: 'none',
                boxShadow: 'none',
              },
            }}
          >
            <Box
              sx={{
                minHeight: 64,
                px: 2,
                py: 1,
                bgcolor: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={config?.companyName || 'Logo'}
                  sx={{ height: 32, maxWidth: 140, objectFit: 'contain' }}
                />
              ) : (
                <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1.05rem' }}>
                  {config?.companyName || 'Chat'}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton
                  onClick={() => setIsTtsActive(!isTtsActive)}
                  size="small"
                  title="Audio"
                  sx={headerIconSx(isTtsActive)}
                >
                  {isTtsActive ? (
                    <VoiceOnIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                  ) : (
                    <VoiceOffIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                  )}
                </IconButton>

                <IconButton
                  onClick={handleHelpClick}
                  size="small"
                  title="Guida"
                  sx={headerIconSx(showHelp)}
                >
                  <HelpOutlineIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                </IconButton>

                <IconButton
                  onClick={() => setShowUserForm(!showUserForm)}
                  size="small"
                  title="Appuntamento"
                  sx={headerIconSx(showUserForm)}
                >
                  <HeadsetMicIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                </IconButton>

                {config?.centralinoEnabled && config?.centralinoPhone && (
                  <IconButton
                    component="a"
                    href={`tel:${String(config.centralinoPhone).replace(/\s+/g, '')}`}
                    size="small"
                    title={config.centralinoHours || 'Chiama il centralino'}
                    sx={headerIconSx(false)}
                  >
                    <PhoneIcon style={{ width: 20, height: 20 }} />
                  </IconButton>
                )}

                <IconButton
                  onClick={toggleVoiceRecognition}
                  size="small"
                  title="Microfono"
                  sx={headerIconSx(isVoiceAssistantActive)}
                >
                  {isVoiceAssistantActive ? (
                    <MicIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                  ) : (
                    <MicOffIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                px: 1.75,
                py: 2,
                position: 'relative',
                bgcolor: '#f4f6f9',
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(15, 23, 42, 0.18)',
                  borderRadius: 8,
                },
                '@media (max-width: 600px)': {
                  px: 1.25,
                },
              }}
            >
              {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
              {messages.map((message, index) => {
                const isUser = message.sender === 'user';
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      mb: 1.5,
                    }}
                  >
                    {!isUser && (
                      <BotAvatar src={avatarUrl} name={config?.companyName} />
                    )}

                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.75,
                        py: 1.25,
                        bgcolor: isUser ? 'primary.main' : '#fff',
                        color: isUser ? '#fff' : 'text.primary',
                        maxWidth: '78%',
                        borderRadius: isUser ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                        boxShadow: isUser
                          ? '0 8px 18px rgba(15, 23, 42, 0.16)'
                          : '0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 16px rgba(15, 23, 42, 0.04)',
                        border: isUser ? 'none' : '1px solid rgba(15, 23, 42, 0.06)',
                        '& a': { color: isUser ? '#fff' : 'secondary.main' },
                        '@media (max-width: 600px)': {
                          maxWidth: '86%',
                        },
                      }}
                    >
                      {message.type === 'video' ? (
                        <video
                          ref={videoRef}
                          width="100%"
                          controls
                          style={{ borderRadius: 12, display: 'block' }}
                          onCanPlay={() => {
                            if (videoRef.current) {
                              videoRef.current.play().catch((error) => {
                                console.error('Errore nella riproduzione del video:', error);
                              });
                            }
                          }}
                          onEnded={() => {
                            startVoiceRecognition();
                          }}
                        >
                          <source src={message.content} type="video/mp4" />
                          Il tuo browser non supporta il tag video.
                        </video>
                      ) : message.options ? (
                        <>
                          <ChatMessage message={message.text} />
                          <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {message.options.map((option, idx) => (
                              <Button
                                key={idx}
                                variant={idx === 0 ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => handleOptionSelected(option.action)}
                                sx={{
                                  borderRadius: 5,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 1.75,
                                  boxShadow: 'none',
                                }}
                              >
                                {option.text}
                              </Button>
                            ))}
                          </Box>
                        </>
                      ) : (
                        renderMessage(message)
                      )}
                    </Paper>
                  </Box>
                );
              })}

              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <BotAvatar src={avatarUrl} name={config?.companyName} />
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#fff',
                      borderRadius: '18px 18px 18px 6px',
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ThreeDots
                      height="28"
                      width="40"
                      radius="9"
                      color={theme.palette.primary.main}
                      ariaLabel="three-dots-loading"
                      visible={true}
                    />
                  </Paper>
                </Box>
              )}

              <div ref={endOfMessagesRef} />
            </Box>

            {showUserForm ? (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                <AppointmentForm
                  onSubmit={handleUserFormSubmit}
                  calendarEnabled={Boolean(config?.calendarEnabled)}
                  availableDays={config?.availableDays || []}
                />
              </LocalizationProvider>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: '#fff',
                  borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                {!isVoiceAssistantActive && (
                  <TextField
                    fullWidth
                    size="small"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleUserInput()}
                    placeholder="Scrivi un messaggio..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '22px',
                        bgcolor: '#f4f6f9',
                        pr: 0.5,
                        '& fieldset': { borderColor: 'transparent' },
                        '&:hover fieldset': { borderColor: 'rgba(15, 23, 42, 0.08)' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={handleUserInput}
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: 'primary.main',
                            color: '#fff',
                            '&:hover': { bgcolor: 'primary.dark' },
                            '& svg': { fill: 'currentColor' },
                          }}
                        >
                          <SendIcon style={{ width: 18, height: 18 }} />
                        </IconButton>
                      ),
                    }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </ThemeProvider>
  );
};

export default ChatBot;
